import { syncManager } from '../../../services/sync';

export interface FinanceLineItem {
    description?: string;
    quantity: number;
    rate: number;
    total: number;
    productId?: string;
    productCode?: string;
}

export interface Quote {
    id: string;
    quoteNumber?: string;
    clientId: string;
    clientName?: string;
    items: FinanceLineItem[];
    subtotal: number;
    tax: number;
    totalTaxes: number;
    total: number;
    status: 'draft' | 'sent' | 'approved' | 'invoiced' | 'partial' | 'overdue' | 'cancelled';
    createdAt: string;
    dueDate?: string;
    taxRate?: number;
    terms?: string;
    banking?: string;
}

export interface Invoice {
    id: string;
    invoiceNumber?: string;
    quoteId?: string;
    clientId: string;
    clientName?: string;
    amount: number;
    balance: number;
    paidToDate: number;
    status: 'unpaid' | 'partial' | 'paid' | 'overdue' | 'draft' | 'sent' | 'cancelled';
    dueDate: string;
    issuedAt: string;
    taxRate?: number;
    totalTaxes?: number;
    terms?: string;
    banking?: string;
    items: Array<{ description: string; quantity: number; rate: number; total: number }>;
    subtotal?: number;
}

export interface FinancialData {
    period: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
    revenue: number;
    expenses: number;
    profit: number;
    mrr: number;
    arr: number;
    growthRate: number;
}

export interface ExpenseItem {
    id: string;
    category: string;
    amount: number;
    date: string;
    description: string;
    optimized: boolean;
}

export interface ReconciliationItem {
    id: string;
    status: 'matched' | 'mismatch' | 'pending' | 'unmatched' | 'partial' | 'voided';
    amount: number;
    source: string;
    destination: string;
    date: string;
}

export interface BudgetVariance {
    category: string;
    period?: string;
    budgeted: number;
    actual: number;
    variance: number;
    status: 'on-track' | 'warning' | 'critical';
}

export interface BudgetEntry {
    id: string;
    category: string;
    period: string;
    budgeted: number;
    actual: number;
    variance: number;
    status: 'on-track' | 'warning' | 'critical';
}

export type PeriodType = 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'custom' | 'all';

export interface CustomPeriod {
    start: string;
    end: string;
}

export function calculatePeriodRange(period: PeriodType, referenceDate: Date = new Date(), customPeriod?: CustomPeriod): { start: string; end: string; label: string } {
    if (period === 'all') {
        return { start: '', end: '', label: 'All Periods' };
    }
    if (period === 'custom' && customPeriod) {
        return { start: customPeriod.start, end: customPeriod.end, label: `Custom (${customPeriod.start} → ${customPeriod.end})` };
    }
    const year = referenceDate.getFullYear();
    const month = referenceDate.getMonth();
    const day = referenceDate.getDate();

    let start: Date;
    let end: Date;
    let label: string;

    switch (period) {
        case 'weekly': {
            const dow = referenceDate.getDay();
            start = new Date(year, month, day - dow);
            end = new Date(year, month, day - dow + 6);
            label = `Week of ${start.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' })}`;
            break;
        }
        case 'monthly':
            start = new Date(year, month, 1);
            end = new Date(year, month + 1, 0);
            label = referenceDate.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
            break;
        case 'quarterly': {
            const q = Math.floor(month / 3);
            start = new Date(year, q * 3, 1);
            end = new Date(year, q * 3 + 3, 0);
            label = `Q${q + 1} ${year}`;
            break;
        }
        case 'yearly':
            start = new Date(year, 0, 1);
            end = new Date(year, 11, 31);
            label = `${year}`;
            break;
        default:
            start = new Date(year, month, 1);
            end = new Date(year, month + 1, 0);
            label = referenceDate.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
    }

    const toISO = (d: Date) => d.toISOString().split('T')[0];
    return { start: toISO(start), end: toISO(end), label };
}

export function isDateInPeriod(dateStr: string, period: PeriodType, referenceDate: Date = new Date(), customPeriod?: CustomPeriod): boolean {
    if (!dateStr) {return false;}
    if (period === 'all') {return true;}
    const { start, end } = calculatePeriodRange(period, referenceDate, customPeriod);
    return dateStr >= start && dateStr <= end;
}

export interface Payment {
    id: string;
    invoiceId?: string;
    amount: number;
    method: string;
    reference: string;
    direction: 'inflow' | 'outflow';
    date: string;
}

export interface Product {
    id: string;
    name: string;
    category: string;
    price: number;
    billingFrequency?: string;
    description?: string;
    deliverableType?: string;
    status?: string;
}

export interface FinancialReport {
    type: 'PnL' | 'BalanceSheet' | 'CashFlow';
    period: string;
    data: Record<string, number>;
    lineItems?: Array<{ label: string; amount: number }>;
    insights: string[];
}

export async function getProducts(): Promise<Product[]> {
    const data = await syncManager.fetchTabData('Master_Products', ADMIN_EMAIL);
    if (!data) {return [];}
    return data.map((row: any) => ({
        id: row.Product_ID || row.SKU || `prod-${Math.random().toString(36).slice(2)}`,
        name: row.Product_Name || row.Name || 'Unnamed Product',
        category: row.Category || 'General',
        price: Number(row.Price_ZAR || row.Price || row.Unit_Price || 0),
        billingFrequency: row.Billing_Frequency || undefined,
        description: row.Description || undefined,
        deliverableType: row.Deliverable_Type || undefined,
        status: row.Status || undefined,
    }));
}

const ADMIN_EMAIL = 'admin@chrysalinks.co.za';

export const financeApi = {
    // --- Quotations ---
    async getQuotes(period?: PeriodType, customPeriod?: CustomPeriod | null): Promise<Quote[]> {
        const data = await syncManager.fetchTabData('Quotes_Log', ADMIN_EMAIL);
        if (!data) {return [];}
        let rows = data;
        if (period) {
            rows = rows.filter((r: any) => isDateInPeriod(r.CreatedAt, period, new Date(), customPeriod ?? undefined));
        }
        return rows.map((row: any) => ({
            id: row.Quote_Number || row.Quote_ID,
            quoteNumber: row.Quote_Number,
            clientId: row.ClientID,
            clientName: row.Client_Name || undefined,
            items: row.Items_JSON ? JSON.parse(row.Items_JSON) : [],
            subtotal: Number(row.Subtotal || 0),
            tax: Number(row.Tax || 0),
            totalTaxes: Number(row.Total_Taxes || 0),
            total: Number(row.Total || 0),
            status: (row.Status || 'draft') as Quote['status'],
            createdAt: row.CreatedAt,
            dueDate: row.Due_Date || undefined,
        }));
    },
    async createQuote(quote: Partial<Quote>): Promise<Quote> {
        const newQuote = {
            ...quote,
            id: quote.quoteNumber || `q-${Date.now()}`,
            status: quote.status || 'draft',
            createdAt: new Date().toISOString().split('T')[0],
        } as Quote;
        await syncManager.logAction('Quotes_Log', 'create', {
            Quote_Number: newQuote.id,
            ClientID: newQuote.clientId,
            Client_Name: newQuote.clientName,
            Subtotal: newQuote.subtotal,
            Tax: newQuote.tax,
            Total_Taxes: newQuote.totalTaxes,
            Total: newQuote.total,
            Status: newQuote.status,
            CreatedAt: newQuote.createdAt,
            Due_Date: newQuote.dueDate,
            Tax_Rate: quote.taxRate,
            Items_JSON: JSON.stringify(newQuote.items || []),
        });
        return newQuote;
    },
    async updateQuoteStatus(id: string, status: Quote['status']): Promise<void> {
        await syncManager.logAction('Quotes_Log', 'update', { Status: status }, id);
    },
    async deleteQuote(id: string): Promise<void> {
        await syncManager.logAction('Quotes_Log', 'delete', null, id);
    },
    async updateQuote(id: string, data: Partial<Quote>): Promise<void> {
        await syncManager.logAction('Quotes_Log', 'update', {
            Quote_Number: data.quoteNumber,
            Client_Name: data.clientName,
            Subtotal: data.subtotal,
            Tax: data.tax,
            Total_Taxes: data.totalTaxes,
            Total: data.total,
            Status: data.status,
            Due_Date: data.dueDate,
            Items_JSON: data.items ? JSON.stringify(data.items) : undefined,
        }, id);
    },

    // --- Invoicing ---
    async getInvoices(period?: PeriodType, customPeriod?: CustomPeriod | null): Promise<Invoice[]> {
        const data = await syncManager.fetchTabData('Invoices_Log', ADMIN_EMAIL);
        if (!data) {return [];}
        let rows = data;
        if (period) {
            rows = rows.filter((r: any) => isDateInPeriod(r.IssuedAt, period, new Date(), customPeriod ?? undefined) || isDateInPeriod(r.DueDate, period, new Date(), customPeriod ?? undefined));
        }
        return rows.map((row: any) => ({
            id: row.Invoice_Number || row.Invoice_ID,
            invoiceNumber: row.Invoice_Number,
            quoteId: row.Quote_ID || undefined,
            clientId: row.ClientID,
            clientName: row.Client_Name || undefined,
            amount: Number(row.Amount || 0),
            balance: Number(row.Balance || 0),
            paidToDate: Number(row.Paid_To_Date || 0),
            status: (row.Status || 'unpaid') as Invoice['status'],
            dueDate: row.DueDate || '',
            issuedAt: row.IssuedAt || '',
            taxRate: Number(row.Tax_Rate || 0),
            totalTaxes: Number(row.Total_Taxes || 0),
            items: row.Items_JSON ? JSON.parse(row.Items_JSON) : [],
            subtotal: Number(row.Subtotal || 0),
        }));
    },
    async createInvoice(invoice: Partial<Invoice>): Promise<Invoice> {
        const newInvoice = {
            ...invoice,
            id: invoice.invoiceNumber || `inv-${Date.now()}`,
            status: invoice.status || 'unpaid',
            issuedAt: new Date().toISOString().split('T')[0],
            items: invoice.items || [],
            subtotal: invoice.subtotal || 0,
        } as Invoice;
        await syncManager.logAction('Invoices_Log', 'create', {
            Invoice_Number: newInvoice.id,
            ClientID: newInvoice.clientId,
            Client_Name: newInvoice.clientName,
            Amount: newInvoice.amount,
            Balance: newInvoice.balance ?? newInvoice.amount,
            Paid_To_Date: newInvoice.paidToDate || 0,
            Status: newInvoice.status,
            DueDate: newInvoice.dueDate,
            IssuedAt: newInvoice.issuedAt,
            Tax_Rate: invoice.taxRate,
            Total_Taxes: invoice.totalTaxes,
            Quote_ID: invoice.quoteId,
            Items_JSON: JSON.stringify(newInvoice.items),
            Subtotal: newInvoice.subtotal,
        });
        return newInvoice;
    },
    async convertQuoteToInvoice(quoteId: string): Promise<Invoice> {
        const quotes = await this.getQuotes();
        const targetQuote = quotes.find(q => q.id === quoteId);
        if (!targetQuote) {
            throw new Error(`Quote ${quoteId} not found`);
        }
        const calcSubtotal = (targetQuote.items || []).reduce((s, it) => s + (it.total || (it.quantity * it.rate)), 0);
        const calcTax = targetQuote.totalTaxes || (calcSubtotal * ((targetQuote.taxRate || 0) / 100));

        return this.createInvoice({
            invoiceNumber: `INV-${targetQuote.quoteNumber || targetQuote.id}-${Date.now().toString().slice(-5)}`,
            quoteId: targetQuote.id,
            clientId: targetQuote.clientId,
            clientName: targetQuote.clientName,
            amount: targetQuote.total,
            balance: targetQuote.total,
            paidToDate: 0,
            status: 'unpaid',
            dueDate: targetQuote.dueDate || new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
            issuedAt: new Date().toISOString().split('T')[0],
            taxRate: targetQuote.taxRate,
            totalTaxes: calcTax,
            items: targetQuote.items.map(item => ({ ...item, description: item.description || '' })),
            subtotal: targetQuote.subtotal || calcSubtotal,
            terms: targetQuote.terms,
            banking: targetQuote.banking,
        });
    },
    async updateInvoiceStatus(id: string, status: Invoice['status']): Promise<void> {
        await syncManager.logAction('Invoices_Log', 'update', { Status: status }, id);
    },
    async deleteInvoice(id: string): Promise<void> {
        await syncManager.logAction('Invoices_Log', 'delete', null, id);
    },
    async updateInvoice(id: string, data: Partial<Invoice> & { taxRate?: number }): Promise<void> {
        await syncManager.logAction('Invoices_Log', 'update', {
            Invoice_Number: data.invoiceNumber,
            Client_Name: data.clientName,
            Amount: data.amount,
            Balance: data.balance,
            Paid_To_Date: data.paidToDate,
            Status: data.status,
            DueDate: data.dueDate,
            Tax_Rate: data.taxRate,
            Items_JSON: data.items ? JSON.stringify(data.items) : undefined,
            Subtotal: data.subtotal,
        }, id);
    },

    // --- Expenses & Optimization ---
    async getExpenses(period?: PeriodType, customPeriod?: CustomPeriod | null): Promise<ExpenseItem[]> {
        const data = await syncManager.fetchTabData('Expenses_Log', ADMIN_EMAIL);
        if (!data) {return [];}
        let rows = data;
        if (period) {
            rows = rows.filter((r: any) => isDateInPeriod(r.Date, period, new Date(), customPeriod ?? undefined));
        }
        return rows.map((row: any) => ({
            id: row.Expense_ID,
            category: row.Category,
            amount: Number(row.Amount || 0),
            date: row.Date,
            description: row.Description,
            optimized: row.Optimized === 'TRUE' || row.Optimized === true,
        }));
    },
    async logExpense(expense: Partial<ExpenseItem>): Promise<ExpenseItem> {
        const newExpense = {
            ...expense,
            id: `e-${Date.now()}`,
            optimized: false
        } as ExpenseItem;
        await syncManager.logAction('Expenses_Log', 'create', {
            Category: newExpense.category,
            Amount: newExpense.amount,
            Date: newExpense.date,
            Description: newExpense.description,
            Optimized: false,
        });
        return newExpense;
    },
    async updateExpense(id: string, data: Partial<ExpenseItem>): Promise<void> {
        await syncManager.logAction('Expenses_Log', 'update', data, id);
    },
    async deleteExpense(id: string): Promise<void> {
        await syncManager.logAction('Expenses_Log', 'delete', null, id);
    },

    // --- Reconciliation ---
    async getReconciliationData(period?: PeriodType, customPeriod?: CustomPeriod | null): Promise<ReconciliationItem[]> {
        const data = await syncManager.fetchTabData('Reconciliation_Log', ADMIN_EMAIL);
        if (!data) {return [];}
        let rows = data;
        if (period) {
            rows = rows.filter((r: any) => isDateInPeriod(r.Date, period, new Date(), customPeriod ?? undefined));
        }
        return rows.map((row: any) => ({
            id: row.Recon_ID,
            status: (row.Status || 'pending') as ReconciliationItem['status'],
            amount: Number(row.Amount || 0),
            source: row.Source,
            destination: row.Destination,
            date: row.Date,
        }));
    },
    async resolveConflict(id: string): Promise<void> {
        await syncManager.logAction('Reconciliation_Log', 'update', { Status: 'matched' }, id);
    },
    async deleteReconciliation(id: string): Promise<void> {
        await syncManager.logAction('Reconciliation_Log', 'delete', null, id);
    },

    // --- Payments ---
    async getPayments(period?: PeriodType, customPeriod?: CustomPeriod | null): Promise<Payment[]> {
        const txData = await syncManager.fetchTabData('Transactions', ADMIN_EMAIL).catch(() => []);
        if (!txData) {return [];}
        let rows = txData;
        if (period) {
            rows = rows.filter((r: any) => isDateInPeriod(r.Date, period, new Date(), customPeriod ?? undefined));
        }
        return rows.map(row => ({
            id: row.Transaction_ID,
            invoiceId: row.Invoice_ID || undefined,
            amount: Number(row.Amount || 0),
            method: row.Payment_Method || 'bank_transfer',
            reference: row.Transaction_Reference || row.Notes || '',
            direction: row.Category === 'expense' || row.Category === 'payout' ? 'outflow' : 'inflow',
            date: row.Date,
        }));
    },
    async logPayment(payment: Partial<Payment>): Promise<Payment> {
        const newPayment = {
            ...payment,
            id: `pay-${Date.now()}`,
            date: payment.date || new Date().toISOString().split('T')[0],
        } as Payment;
        await syncManager.logAction('Transactions', 'create', {
            Amount: newPayment.amount,
            Payment_Method: newPayment.method,
            Transaction_Reference: newPayment.reference,
            Category: newPayment.direction === 'inflow' ? 'payment_income' : 'expense',
            Date: newPayment.date,
        });
        return newPayment;
    },
    async updatePayment(id: string, data: Partial<Payment>): Promise<void> {
        await syncManager.logAction('Transactions', 'update', {
            Amount: data.amount,
            Payment_Method: data.method,
            Transaction_Reference: data.reference,
        }, id);
    },
    async deletePayment(id: string): Promise<void> {
        await syncManager.logAction('Transactions', 'delete', null, id);
    },
    async linkPaymentToInvoice(paymentId: string, invoiceId: string): Promise<void> {
        await syncManager.logAction('Transactions', 'update', { Invoice_ID: invoiceId }, paymentId);
    },
    async sendPaymentReminder(invoiceId: string): Promise<void> {
        const invoice = await syncManager.fetchTabData('Invoices_Log', ADMIN_EMAIL).then(rows =>
            rows?.find((r: any) => r.Invoice_Number === invoiceId || r.Invoice_ID === invoiceId)
        );
        if (!invoice) {return;}
        await syncManager.logAction('Transactions', 'create', {
            Category: 'reminder',
            Amount: 0,
            Payment_Method: 'reminder',
            Transaction_Reference: `Reminder sent for ${invoice.Invoice_Number || invoice.Invoice_ID}`,
            Date: new Date().toISOString().split('T')[0],
            Invoice_ID: invoice.Invoice_Number || invoice.Invoice_ID,
            Notes: `Reminder sent to ${invoice.Client_Name || invoice.ClientID} for R ${invoice.Amount}`,
        });
    },

    // --- Intelligence & Reports ---
    async getFinancialData(period: string, customPeriod?: CustomPeriod | null): Promise<FinancialData> {
        const data = await syncManager.fetchTabData('Financial_Intelligence', ADMIN_EMAIL);
        if (data && data.length > 0) {
            let filtered = data;
            if (period === 'custom' && customPeriod) {
                filtered = data.filter((r: any) => {
                    const d = r.Date || r.Period || r.date || '';
                    return d >= customPeriod.start && d <= customPeriod.end;
                });
            } else if (period !== 'all') {
                const { start, end } = calculatePeriodRange(period as PeriodType, new Date(), customPeriod ?? undefined);
                filtered = data.filter((r: any) => {
                    const d = r.Date || r.Period || r.Date || '';
                    return d >= start && d <= end;
                });
            }
            const totalRevenue = filtered.reduce((sum: number, row: any) => sum + Number(row.Revenue || 0), 0);
            const totalExpenses = filtered.reduce((sum: number, row: any) => sum + Number(row.Expenses || 0), 0);
            const latest = filtered[filtered.length - 1];
            return {
                period: (period || 'monthly') as FinancialData['period'],
                revenue: totalRevenue,
                expenses: totalExpenses,
                profit: totalRevenue - totalExpenses,
                mrr: Number(latest?.MRR || totalRevenue),
                arr: Number(latest?.ARR || totalRevenue * 12),
                growthRate: Number(latest?.GrowthRate || 0),
            };
        }
        // Fallback: derive from Transactions + Expenses_Log
        const [txData, expensesData] = await Promise.all([
            syncManager.fetchTabData('Transactions', ADMIN_EMAIL).catch(() => []),
            syncManager.fetchTabData('Expenses_Log', ADMIN_EMAIL).catch(() => []),
        ]);
        const revenue = (txData || []).reduce((sum: number, r: any) => sum + Number(r.Amount || 0), 0);
        const expenses = (expensesData || []).reduce((sum: number, r: any) => sum + Number(r.Amount || 0), 0);
        return {
            period: (period || 'monthly') as FinancialData['period'],
            revenue,
            expenses,
            profit: revenue - expenses,
            mrr: Math.round(revenue * 100) / 100,
            arr: Math.round(revenue * 12 * 100) / 100,
            growthRate: 0,
        };
    },
    async getBudgets(period?: PeriodType): Promise<BudgetEntry[]> {
        const data = await syncManager.fetchTabData('Budgets', ADMIN_EMAIL);
        if (!data) {return [];}
        let rows = data;
        if (period) {
            rows = rows.filter((r: any) => r.Period === period || r.Period === 'all');
        }
        return rows.map((row: any, idx: number) => ({
            id: `budget-${idx}-${row.Category}`,
            category: row.Category,
            period: row.Period || 'all',
            budgeted: Number(row.Budgeted || 0),
            actual: Number(row.Actual || 0),
            variance: Number(row.Variance || 0),
            status: (row.Status || 'on-track') as BudgetEntry['status'],
        }));
    },
    async addBudget(entry: Partial<BudgetEntry> & { category: string; budgeted: number; period: string }): Promise<BudgetEntry> {
        const newEntry = {
            id: `budget-${Date.now()}`,
            category: entry.category,
            period: entry.period,
            budgeted: entry.budgeted,
            actual: 0,
            variance: 0,
            status: 'on-track' as const,
        };
        await syncManager.logAction('Budgets', 'create', {
            Category: newEntry.category,
            Period: newEntry.period,
            Budgeted: newEntry.budgeted,
            Actual: 0,
            Variance: 0,
            Status: 'on-track',
        });
        return newEntry;
    },
    async getBudgetVariances(period?: PeriodType): Promise<BudgetVariance[]> {
        const budgets = await this.getBudgets(period);
        return budgets.map(b => ({
            category: b.category,
            period: b.period,
            budgeted: b.budgeted,
            actual: b.actual,
            variance: b.variance,
            status: b.status,
        }));
    },
    async getFinancialReport(type: FinancialReport['type'], period: string, customPeriod?: CustomPeriod | null): Promise<FinancialReport> {
        const [intelligenceData, expensesData, invoicesData, quotesData, paymentsData] = await Promise.all([
            syncManager.fetchTabData('Financial_Intelligence', ADMIN_EMAIL).catch(() => []),
            syncManager.fetchTabData('Expenses_Log', ADMIN_EMAIL).catch(() => []),
            syncManager.fetchTabData('Invoices_Log', ADMIN_EMAIL).catch(() => []),
            syncManager.fetchTabData('Quotes_Log', ADMIN_EMAIL).catch(() => []),
            syncManager.fetchTabData('Transactions', ADMIN_EMAIL).catch(() => []),
        ]);

        const periodType = period as PeriodType;
        const filterByDate = (rows: any[], dateField: string) => {
            if (periodType === 'all') {return rows;}
            if (periodType === 'custom' && customPeriod) {
                return rows.filter((r: any) => {
                    const d = r[dateField] || '';
                    return d >= customPeriod.start && d <= customPeriod.end;
                });
            }
            const { start, end } = calculatePeriodRange(periodType, new Date(), customPeriod ?? undefined);
            return rows.filter((r: any) => {
                const d = r[dateField] || '';
                return d >= start && d <= end;
            });
        };

        const filteredIntelligence = filterByDate(intelligenceData || [], 'Date');
        const filteredExpenses = filterByDate(expensesData || [], 'Date');
        const filteredInvoices = filterByDate(invoicesData || [], 'IssuedAt');
        const filteredQuotes = filterByDate(quotesData || [], 'CreatedAt');
        const filteredPayments = filterByDate(paymentsData || [], 'Date');
        const revenue = filteredIntelligence.reduce((sum: number, row: any) => sum + Number(row.Revenue || 0), 0);
        const expenses = filteredExpenses.reduce((sum: number, row: any) => sum + Number(row.Amount || 0), 0);
        const invoiced = filteredInvoices.reduce((sum: number, row: any) => sum + Number(row.Amount || 0), 0);
        const quoted = filteredQuotes.reduce((sum: number, row: any) => sum + Number(row.Total || 0), 0);
        const paidAmount = filteredPayments
            .filter((r: any) => r.Category === 'payment_income' || r.Direction === 'inflow')
            .reduce((sum: number, r: any) => sum + Number(r.Amount || 0), 0);
        const unpaidAmount = invoiced - paidAmount;
        const netProfit = revenue - expenses;

        const expenseByCategory: Record<string, number> = {};
        (filteredExpenses || []).forEach((r: any) => {
            const cat = r.Category || 'Uncategorized';
            expenseByCategory[cat] = (expenseByCategory[cat] || 0) + Number(r.Amount || 0);
        });

        const invoiceLineItems = (filteredInvoices || []).map((r: any) => ({
            label: `${r.Invoice_Number || r.Invoice_ID || 'Invoice'} — ${r.Client_Name || r.ClientID || 'Client'}`,
            amount: Number(r.Amount || 0),
        }));

        const expenseLineItems = Object.entries(expenseByCategory).map(([label, amount]) => ({
            label,
            amount,
        }));

        let data: Record<string, number> = {};
        let lineItems: Array<{ label: string; amount: number }> = [];
        let insights: string[] = [];

        switch (type) {
            case 'PnL':
                data = { revenue, expenses, netProfit, grossMargin: revenue > 0 ? ((revenue - expenses) / revenue) * 100 : 0 };
                lineItems = [
                    { label: 'Revenue', amount: revenue },
                    ...expenseLineItems,
                    { label: 'Net Profit', amount: netProfit },
                ];
                insights = [
                    revenue > 0 ? `Revenue for ${period}: R ${revenue.toLocaleString()}` : 'No revenue data available',
                    netProfit > 0 ? `Net profit margin: ${((netProfit / revenue) * 100).toFixed(1)}%` : 'Operating at a loss',
                    expenses > 0 ? `Total expenses: R ${expenses.toLocaleString()}` : 'No expense data recorded',
                ];
                break;
            case 'BalanceSheet':
                data = { assets: paidAmount + revenue, liabilities: unpaidAmount, equity: netProfit, totalInvoiced: invoiced };
                lineItems = [
                    ...invoiceLineItems,
                    { label: 'Outstanding Receivables', amount: unpaidAmount },
                    { label: 'Cash Collected', amount: paidAmount },
                ];
                insights = [
                    `Total invoiced: R ${invoiced.toLocaleString()}`,
                    `Outstanding receivables: R ${unpaidAmount.toLocaleString()}`,
                    `Cash collected: R ${paidAmount.toLocaleString()}`,
                ];
                break;
            case 'CashFlow':
                data = { inflow: paidAmount, outflow: expenses, netCashFlow: paidAmount - expenses, pipelineValue: quoted };
                lineItems = [
                    { label: 'Cash Inflow (Payments Received)', amount: paidAmount },
                    { label: 'Cash Outflow (Expenses)', amount: expenses },
                    { label: 'Net Cash Flow', amount: paidAmount - expenses },
                    { label: 'Quoted Pipeline (unbilled)', amount: quoted },
                ];
                insights = [
                    `Cash inflow: R ${paidAmount.toLocaleString()}`,
                    `Cash outflow: R ${expenses.toLocaleString()}`,
                    `Net cash flow: R ${(paidAmount - expenses).toLocaleString()}`,
                    `Quoted pipeline: R ${quoted.toLocaleString()}`,
                ];
                break;
        }

        return { type, period, data, lineItems, insights };
    },
    // --- Products ---
    async getProducts(): Promise<Product[]> {
        return getProducts();
    },
    async exportReport(report: FinancialReport): Promise<string> {
        const csvRows = Object.entries(report.data).map(([k, v]) => `${k},${v}`);
        const csvContent = `Report: ${report.type}\nPeriod: ${report.period}\n\n${csvRows.join('\n')}`;
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${report.type}_${report.period}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        return url;
    }
};
